'use client';

import { AUTH_REDIRECT_QUERY_PARAMS } from '@shared/tracking';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useEffectEvent, useState } from 'react';
import { AccountClosedAlertDialog } from './AccountClosed/AccountClosedAlertDialog';
import { BannedAlertDialog } from './Banned/BannedAlertDialog';
import { FraudExcludedAlertDialog } from './FraudExcluded/FraudExcludedAlertDialog';
import { LoginErrorAlertDialog } from './LoginError/LoginErrorAlertDialog';
import { ReKycAlertDialog } from './ReKyc/ReKycAlertDialog';
import type { RequiredActionConfiguration, RequiredActionTriggerParamValue } from './types';

function matchesTriggerParams(params: URLSearchParams, triggerParams: Record<string, RequiredActionTriggerParamValue>): boolean {
  return Object.entries(triggerParams).every(([key, value]) => {
    const paramValue = params.get(key);
    if (paramValue == null) return false;

    const allowedValues = Array.isArray(value) ? value : [value];
    return allowedValues.includes(paramValue);
  });
}

const REQUIRED_ACTIONS: RequiredActionConfiguration[] = [
  {
    id: 'fraud-excluded',
    triggerParams: {
      login_success: 'false',
      error: 'authorization',
      error_description: 'fraud_excluded_seon',
    },
    Component: FraudExcludedAlertDialog,
  },
  {
    id: 'banned',
    triggerParams: {
      login_success: 'false',
      error: 'authorization',
      error_description: ['login_no_possible_banned', 'login_no_possible_duplicate_suspicion'],
    },
    Component: BannedAlertDialog,
  },
  {
    id: 'account-closed',
    triggerParams: {
      login_success: 'false',
      error: 'authorization',
      error_description: 'login_no_possible_deleted',
    },
    Component: AccountClosedAlertDialog,
  },
  {
    id: 'network-error-login',
    triggerParams: {
      login_success: 'false',
      error: 'authorization',
      error_description: 'login_no_possible',
    },
    Component: LoginErrorAlertDialog,
  },
  {
    id: 'network-error-server',
    triggerParams: {
      login_success: 'false',
      error: 'authorization',
      error_description: 'server_error',
    },
    Component: LoginErrorAlertDialog,
  },
  {
    id: 'rekyc',
    triggerParams: {
      login_success: 'false',
      required_action: 'rekyc',
    },
    Component: ReKycAlertDialog,
  },
];

function RequiredActions() {
  const pathname = usePathname();
  const { replace } = useRouter();
  const [open, setOpen] = useState<RequiredActionConfiguration | null>(null);
  const [playerId, setPlayerId] = useState<string | undefined>();

  // Reading the auth params from window.location after mount (instead of useSearchParams)
  // keeps this layout-level component from bailing every page to client-side rendering.
  // See docs/guides/avoiding-csr-bailout.md (Option 1).
  const triggerRequiredAction = useEffectEvent(() => {
    const params = new URLSearchParams(window.location.search);
    const action = REQUIRED_ACTIONS.find(({ triggerParams }) => matchesTriggerParams(params, triggerParams)) ?? null;
    if (!action) return;

    setPlayerId(params.get(AUTH_REDIRECT_QUERY_PARAMS.PLAYER_ID) ?? undefined);
    setOpen(action);

    // Strip the params that triggered the dialog (plus playerId) so a refresh doesn't
    // re-open it, while leaving any unrelated query params untouched.
    [...Object.keys(action.triggerParams), AUTH_REDIRECT_QUERY_PARAMS.PLAYER_ID].forEach(key => params.delete(key));
    replace(params.size ? `${pathname}?${params}` : pathname, { scroll: false });
  });

  useEffect(() => {
    triggerRequiredAction();
  }, []);

  return REQUIRED_ACTIONS.map(({ id, Component }) => (
    <Component key={id} open={open?.id === id} onOpenChange={isOpen => !isOpen && setOpen(null)} playerId={open?.id === id ? playerId : undefined} />
  ));
}

export { RequiredActions };
